// The AI-free "manually" form (docs/project.md §7): the typed spelling becomes an empty lexeme,
// translations and examples go into the entry overrides.
import { useState } from 'react';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useTranslations } from 'use-intl';
import { z } from 'zod';
import { api } from '@/api/endpoints';
import { ApiRequestError } from '@/api/client';
import { db } from '@/db/db';
import { applyMutationRows } from '@/sync/apply';
import { puller } from '@/sync/pull';
import { useDictionary, refreshDictionaries } from '@/state/session';
import { useOnline } from '@/state/online';
import { EntryForm, cleanDraft, emptyDraft, type EntryDraft } from '@/components/EntryForm';
import { ErrorText, Sheet, SheetBar, useCloseOverlay } from '@/components/ui';
import { reportError } from '@/lib/errors';
import { track } from '@/lib/analytics';
import { addResult } from '@/state/pending';

export const Route = createFileRoute('/_app/d/$dictionaryId/add')({
  validateSearch: z.object({ headword: z.string().optional() }),
  component: ManualAddScreen,
});

function ManualAddScreen() {
  const { dictionaryId } = Route.useParams();
  const { headword } = Route.useSearch();
  const t = useTranslations('edit');
  const tc = useTranslations('common');
  const navigate = useNavigate();
  const online = useOnline();
  const dictionary = useDictionary(dictionaryId);
  const close = useCloseOverlay({ to: '/d/$dictionaryId', params: { dictionaryId } });
  const [draft, setDraft] = useState<EntryDraft>(() => emptyDraft(headword ?? ''));
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [headwordError, setHeadwordError] = useState<string | null>(null);
  if (!dictionary) return null;

  const save = async () => {
    const clean = cleanDraft(draft);
    if (!clean.headword) {
      setHeadwordError(t('errors.headwordRequired'));
      return;
    }
    setBusy(true);
    setError(null);
    try {
      const res = await api.entries.addManual(dictionaryId, {
        headword: clean.headword,
        partOfSpeech: clean.partOfSpeech ?? undefined,
        transcription: clean.transcription || undefined,
        translations: (clean.translations ?? []).map((x) => ({ text: x.text, contextLabel: x.contextLabel })),
        examples: (clean.examples ?? []).map((x) => ({ text: x.text, translation: x.translation })),
      });
      await applyMutationRows(db, res);
      track('word_added', {
        source: 'manual',
        result: addResult(res),
        typed: clean.headword,
        headword: res.lexeme.headword,
        entry_id: res.entry.id,
      });
      void puller.pull(dictionaryId);
      void refreshDictionaries();
      void navigate({ to: '/d/$dictionaryId/w/$entryId', params: { dictionaryId, entryId: res.entry.id }, replace: true });
    } catch (e) {
      reportError(e);
      track('word_add_failed', {
        source: 'manual',
        reason: e instanceof ApiRequestError ? e.code : 'unknown',
        typed: clean.headword,
      });
      setError(e instanceof ApiRequestError && e.isNetwork ? tc('errorNetwork') : tc('errorGeneric'));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Sheet onClose={close} label={t('manualTitle')} width="max-w-[640px]">
      <SheetBar
        left={
          <button type="button" className="h-11 px-1 text-[17px] text-accent" onClick={close}>
            {tc('cancel')}
          </button>
        }
        title={t('manualTitle')}
        right={
          <button type="button" className="h-11 px-1 text-[17px] font-semibold text-accent disabled:opacity-40" disabled={busy || !online} data-testid="save" onClick={() => void save()}>
            {tc('save')}
          </button>
        }
      />
      <div className="flex flex-grow flex-col gap-4 overflow-y-auto px-4 pb-8 pt-2">
        <EntryForm mode="manual" draft={draft} onChange={setDraft} shared={{ lexeme: null, gloss: null }} targetLang={dictionary.targetLang} baseLang={dictionary.baseLang} headwordError={headwordError} />
        <ErrorText>{error}</ErrorText>
      </div>
    </Sheet>
  );
}
